<template>
    <SQLViewsModal
        v-show="openTransformSQLModal"
        :scripts="[{ scriptValue: transformForModal?.sql_query || '', scriptName: transformForModal?.name }]"
        :is-modal-open="openTransformSQLModal"
        @update:is-open="setSQLModalOpenStatus($event)" />

    <div class="mx-auto w-full">
        <div>
            <div class="flex justify-between items-center mb-6">
                <div class="flex gap-3 items-center">
                    <Button v-can-access.permissions="[$Permissions.WRITE_TRANSFORMS]" variant="outline" class="gap-2" @click="onCreateTransform">
                        <PlusCircle class="h-4 w-4" />
                        Create
                    </Button>
                    <SearchInput
                        :loading="configLoading || transformTableDataLoading"
                        placeholder="Search by name..."
                        @search="(input) => (searchQuery = input)" />
                </div>
            </div>

            <div class="mt-2">
                <ScrollArea
                    class="w-full border shadow-md rounded-md"
                    :class="
                        transformTableDataLoading ||
                        (configLoading && 10 <= 10) ||
                        (!transformTableDataLoading && !configLoading && (transformsWithLatestMetrics?.length || 0) <= 10)
                            ? 'h-auto'
                            : 'h-70-dynamic'
                    ">
                    <Table>
                        <TableHeader class="sticky top-0 bg-background z-10 border-b">
                            <TableRow class="bg-muted/50">
                                <TableHead class="first:rounded-tl-md">Name</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Start Time</TableHead>
                                <TableHead>Duration</TableHead>
                                <TableHead>End Time</TableHead>
                                <TableHead>Latency</TableHead>
                                <TableHead>Tasks</TableHead>
                                <TableHead class="last:rounded-tr-md"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <!-- Skeleton rows when loading -->
                            <template v-if="transformTableDataLoading || configLoading">
                                <TableRow v-for="n in 10" :key="`skeleton-${n}`">
                                    <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-type" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-status" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-status" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-metric" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-status" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-number" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-number" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                                </TableRow>
                            </template>
                            <!-- Actual data rows -->
                            <template v-else>
                                <TableRow v-for="(transform, index) in transformsWithLatestMetrics" :key="transform.id">
                                    <TableCell>
                                        <Button
                                            variant="link"
                                            class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                            :title="transform.name"
                                            @click="$router.push({ name: 'transform-details', params: { id: transform.id } })">
                                            <span class="truncate max-w-[200px] sm:max-w-[250px] md:max-w-[300px] lg:max-w-[350px] xl:max-w-[400px]">
                                                {{ transform.name }}
                                            </span>
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        <div class="flex items-center gap-2">
                                            <Avatar
                                                v-if="transform.transform_type"
                                                class="h-8 w-10 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                                <AvatarImage
                                                    :src="getTransformIcon(transform.transform_type)"
                                                    alt="transform icon"
                                                    class="object-contain p-0.5" />
                                                <AvatarFallback
                                                    class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                                                    {{ transform.transform_type?.charAt(0).toUpperCase() }}
                                                </AvatarFallback>
                                            </Avatar>
                                            <span class="text-xs text-muted-foreground">{{
                                                transformTypeDisplayName(transform.transform || transform.transform_type)
                                            }}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            :variant="getBadgeVariantAndClasses(transform.metrics?.status).variant"
                                            :class="getBadgeVariantAndClasses(transform.metrics?.status).classes">
                                            {{ transform.metrics?.status?.toUpperCase() || 'UNKNOWN' }}
                                        </Badge>
                                    </TableCell>
                                    <TableCell
                                        :variant="
                                            transformTableDataLoading ||
                                            configLoading ||
                                            getFormattedValue(
                                                'start_time',
                                                transform.metrics?.start_time,
                                                transform.transform || transform.transform_type
                                            ).display === '-'
                                                ? 'default'
                                                : 'metric-badge'
                                        ">
                                        <SkeletonLoader v-if="transformTableDataLoading || configLoading" preset="table-status" :loading="true" />
                                        <template v-else>
                                            {{
                                                getFormattedValue(
                                                    'start_time',
                                                    transform.metrics?.start_time,
                                                    transform.transform || transform.transform_type
                                                ).display
                                            }}
                                        </template>
                                    </TableCell>
                                    <TableCell
                                        :variant="
                                            transformTableDataLoading ||
                                            configLoading ||
                                            getFormattedValue(
                                                'duration',
                                                transform.metrics?.duration,
                                                transform.transform || transform.transform_type
                                            ).display === '-'
                                                ? 'default'
                                                : 'metric-badge'
                                        ">
                                        <SkeletonLoader v-if="transformTableDataLoading || configLoading" preset="table-status" :loading="true" />
                                        <template v-else>
                                            {{
                                                getFormattedValue(
                                                    'duration',
                                                    transform.metrics?.duration,
                                                    transform.transform || transform.transform_type
                                                ).display
                                            }}
                                        </template>
                                    </TableCell>
                                    <TableCell
                                        :variant="
                                            transformTableDataLoading ||
                                            configLoading ||
                                            getFormattedValue(
                                                'end_time',
                                                transform.metrics?.end_time,
                                                transform.transform || transform.transform_type
                                            ).display === '-'
                                                ? 'default'
                                                : 'metric-badge'
                                        ">
                                        <SkeletonLoader v-if="transformTableDataLoading || configLoading" preset="table-status" :loading="true" />
                                        <template v-else>
                                            {{
                                                getFormattedValue(
                                                    'end_time',
                                                    transform.metrics?.end_time,
                                                    transform.transform || transform.transform_type
                                                ).display
                                            }}
                                        </template>
                                    </TableCell>
                                    <TableCell
                                        :variant="
                                            transformTableDataLoading ||
                                            configLoading ||
                                            getFormattedValue('latency', transform.metrics?.latency, transform.transform || transform.transform_type)
                                                .display === '-'
                                                ? 'default'
                                                : 'metric-badge'
                                        ">
                                        <SkeletonLoader v-if="transformTableDataLoading || configLoading" preset="table-status" :loading="true" />
                                        <template v-else>
                                            {{
                                                getFormattedValue(
                                                    'latency',
                                                    transform.metrics?.latency,
                                                    transform.transform || transform.transform_type
                                                ).display
                                            }}
                                        </template>
                                    </TableCell>
                                    <TableCell
                                        :variant="
                                            transformTableDataLoading ||
                                            configLoading ||
                                            getFormattedValue('tasks', transform.metrics?.tasks, transform.transform || transform.transform_type)
                                                .display === '-'
                                                ? 'default'
                                                : 'metric-badge'
                                        ">
                                        <SkeletonLoader v-if="transformTableDataLoading || configLoading" preset="table-status" :loading="true" />
                                        <template v-else>
                                            {{
                                                getFormattedValue('tasks', transform.metrics?.tasks, transform.transform || transform.transform_type)
                                                    .display
                                            }}
                                        </template>
                                    </TableCell>
                                    <TableCell>
                                        <ActionMenu
                                            variant="menu"
                                            :show-copy-id="true"
                                            :show-view="true"
                                            :show-delete="true"
                                            :show-clone="userHasAccess([$Permissions.WRITE_TRANSFORMS], authStore.user)"
                                            :loading="transformTableDataLoading || configLoading || actionPerItemLoading[transform.id]"
                                            @copyId="copyId(transform.id)"
                                            @view="onTransformSelected(transform.id)"
                                            @delete="onDelete(transform)"
                                            @clone="onClone(transform)" />
                                    </TableCell>
                                </TableRow>
                                <TableEmpty v-if="!transformTableDataLoading && !configLoading && !transformTableData?.length" :colspan="9">
                                    <TableEmptyState />
                                </TableEmpty>
                            </template>
                        </TableBody>
                    </Table>
                    <ScrollBar orientation="horizontal" />
                    <ScrollBar orientation="vertical" />
                </ScrollArea>
            </div>
            <TablePagination
                v-if="transformTableData.length > 0 || page > 0"
                :current-page="page"
                :page-size="pageSize"
                :total-items="transformsStore.total"
                :current-page-items-count="transformTableData?.length || 0"
                item-type="transforms"
                :loading="transformTableDataLoading || configLoading"
                @page-change="
                    (newPage) => {
                        page = newPage;
                    }
                "
                @page-size-change="onItemsPerPageChange" />
        </div>
    </div>
</template>

<script setup>
defineOptions({
    name: 'TransformsPage'
});

import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import _ from 'lodash';
import AppLoader from '../../components/AppLoader.vue';

import { useTransformsStore } from '@/stores/transforms.store';
import { useMetricsStore } from '@/stores/metrics.store';
import WebSocketService from '../../services/webSocket.service';
import SearchInput from '@/components/global/SearchInput.vue';
import { formatter, getStatusColor, getMetricFormattingInfo } from '../../utils/utils';
import { useUrlFilters } from '@/composables/useUrlFilters';
import { FILTER_DEFAULTS } from '../../utils/constants';
import SQLViewsModal from '@/components/SQLViewsModal.vue';
import { useDeleteConfirmation } from '@/composables/useDialog';
import { useAuthStore } from '@/stores/auth.store';
import { userHasAccess } from '@/utils/utils';
import { toast } from '@/composables/useToast';

import ActionMenu from '@/components/ActionMenu.vue';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import { PlusCircle } from 'lucide-vue-next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import TablePagination from '@/components/TablePagination.vue';
import { getBadgeVariantAndClasses } from '../../utils/utils';
import { useRouter } from 'vue-router';
import { getTransformIcon } from '@/utils/constants';
// import SortingButton from '../../components/global/SortingButton.vue';
// import RowActionMenu from '@/components/RowActionMenu.vue';
// import moment from 'moment';
// import ComingSoon from '@/components/global/ComingSoon.vue';

const transformsStore = useTransformsStore();
const metricsStore = useMetricsStore();
const router = useRouter();
const authStore = useAuthStore();

const { registerFilter } = useUrlFilters({
    defaults: {
        // Pagination
        page: 0,
        pageSize: 10,

        // Search
        searchQuery: null
    },

    // Custom serializers for complex objects
    serializers: {
        searchQuery: (value) => value || null
    },

    // Custom deserializers
    deserializers: {
        searchQuery: (value) => value || null
    }
});

// Register all filters and get reactive references
const page = registerFilter('page', 0);
const pageSize = registerFilter('pageSize', 10);
const searchQuery = registerFilter('searchQuery', null);

const transformTableData = ref([]);
const configLoading = ref(false);
const transformTableDataLoading = ref(false);
const actionPerItemLoading = ref({});

const openTransformSQLModal = ref(false);

const transformInEdit = ref({});
const transformForModal = ref({});
// const selectedTransform = ref(null);
// const sortColumn = ref('name');
// const sortDirection = ref(false);
// const selectAllState = ref(false);
// const checkedTransforms = ref({});

onMounted(async () => {
    configLoading.value = true;

    try {
        await Promise.all([
            fetchTransformsTableData(),
            transformsStore.fetchTransformConfigs().then(() => {
                configLoading.value = false;
            })
        ]);

        addWebsocketListeners();
    } catch (error) {
        console.error('Transform page error:', error);
    } finally {
        configLoading.value = false;
    }
});

const addWebsocketListeners = () => {
    WebSocketService.addListener({
        topic: 'metrics',
        entityType: 'transforms',
        entityLevels: ['inline'],
        timeType: 'latest',
        entityId: null,
        callback: metricsStore.onMetricsData,
        callbackId: 'metrics.onMetricsData'
    });
};

const removeWebsocketListeners = () => {
    WebSocketService.removeListener({
        topic: 'metrics',
        entityType: 'transforms',
        entityLevels: ['inline'],
        timeType: 'latest',
        entityId: null,
        callbackId: 'metrics.onMetricsData'
    });
};

// onBeforeUnmount(() => {
//     removeWebsocketListeners();
// });

// Automatic data refetching watchers
watch(
    [page, pageSize],
    async () => {
        transformTableDataLoading.value = true;
        await fetchTransformsTableData(page.value, pageSize.value);
    },
    { deep: true }
);

// Debounced search watcher
watch(
    () => searchQuery.value,
    _.debounce(async () => {
        page.value = 0; // Reset to first page on search
        transformTableDataLoading.value = true;
        await fetchTransformsTableData(0, pageSize.value, searchQuery.value);
    }, 700)
);

async function fetchTransformsTableData(newPage = null, newPageSize = null, currentSearchQuery = null, forceFetch = false) {
    transformTableDataLoading.value = true;

    try {
        page.value = _.isNil(newPage) ? page.value : newPage;
        pageSize.value = _.isNil(newPageSize) ? pageSize.value : newPageSize;

        if (currentSearchQuery || currentSearchQuery === '') {
            page.value = 0;
        }

        await transformsStore
            .fetch({
                page_size: pageSize.value,
                page_number: page.value,
                partial_name: currentSearchQuery || searchQuery.value,
                forceFetch
            })
            .then(() => {
                transformTableData.value = transformsStore.all;
                transformTableDataLoading.value = false;
            });
    } finally {
        transformTableDataLoading.value = false;
    }
}

const getFormattedValue = (metricKey, value, transformType) => {
    const configs = Object.values(transformsStore.configs).find((x) => x.filter((y) => (y.transform || y.transform_type) === transformType).length);

    const metricInfo = getMetricFormattingInfo(metricKey, configs?.[0]?.metrics);

    return formatter(value, metricInfo || {});
};

async function onItemsPerPageChange(newPageSize) {
    try {
        page.value = 0;
        pageSize.value = newPageSize;
        // The watcher will automatically trigger fetchTransformsTableData
    } catch (error) {
        console.error('Transform page error:', error);
    }
}

const cancelEditTransformName = () => {
    transformInEdit.value = {};
};

const editTransformName = (transform) => {
    transformInEdit.value = {
        id: transform.id,
        name: transform.name
    };
};

const saveTransformName = async (transformId) => {
    try {
        actionPerItemLoading.value[transformId] = true;

        await transformsStore.editTransform(transformInEdit.value);

        transformTableData.value = transformsStore.all;
        transformInEdit.value = {};
    } catch (error) {
        console.error('Transform page error:', error);
    } finally {
        actionPerItemLoading.value[transformId] = false;
    }
};

const transformsWithLatestMetrics = computed(() => {
    return (
        transformTableData.value?.map((transform) => {
            return {
                ...transform,
                metrics: {
                    ...transform.metrics,
                    ...(metricsStore.transforms.allLatest?.[transform.id]?.inline || {})
                }
            };
        }) || []
    );
});

const setSQLModalOpenStatus = (status, transform = {}) => {
    transformForModal.value = transform;
    openTransformSQLModal.value = status;
};

const copyId = (id) => {
    navigator.clipboard.writeText(id);
};

const onCreateTransform = () => {
    return router.push({ name: 'create-transform' });
};

const onTransformSelected = (id) => {
    return router.push({ name: 'transform-details', params: { id } });
};

const transformTypeDisplayName = (transformType) => {
    const configs = Object.values(transformsStore.configs).find((x) => x.filter((y) => (y.transform || y.transform_type) === transformType).length);

    return configs?.[0]?.display_name;
};

async function onClone(transform) {
    try {
        actionPerItemLoading.value[transform.id] = true;
        const clonedTransform = await transformsStore.clone(transform.id);

        // Navigate to the new cloned transform
        router.push({ name: 'transform-details', params: { id: clonedTransform.id } });

        toast.success(`Transform "${transform.name}" successfully cloned.`);
    } catch (error) {
        console.error('Clone failed:', error);
        toast.error('Failed to clone transform.');
    } finally {
        actionPerItemLoading.value[transform.id] = false;
    }
}

async function onDelete(transform) {
    actionPerItemLoading.value[transform.id] = true;

    try {
        await useDeleteConfirmation({
            entity: 'transform',
            name: transform.name,
            title: 'Delete Transform',
            message: `Are you sure you want to delete the transform <strong>"${transform.name}"</strong>? This action cannot be undone.`,
            onConfirm: async () => {
                await transformsStore.delete(transform.id);
                // Refresh the transforms list after successful deletion
                fetchTransformsTableData(page.value, pageSize.value, searchQuery.value, true);
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Delete failed:', error);
        }
    } finally {
        actionPerItemLoading.value[transform.id] = false;
    }
}
</script>
